import { Box, PopperPlacementType, Tooltip, Typography, TypographyProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface EllipsisTextProps extends TypographyProps {
  maxWidth?: string;
  disableHoverListener?: boolean;
  delay?: number;
  enterNextDelay?: number;
  tooltipText?: string;
  placement?: PopperPlacementType;
  isMultiLine?: boolean;
  linesNumber?: number;
}

export const EllipsisText = ({
  children,
  maxWidth,
  disableHoverListener = false,
  delay = 100,
  enterNextDelay,
  tooltipText,
  placement = 'auto',
  isMultiLine = false,
  linesNumber = 2,
  sx,
  ...props
}: EllipsisTextProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const currentRef = textRef.current;
    const checkOverflow = () => {
      if (currentRef) {
        if (isMultiLine) {
          setIsOverflowing(currentRef.scrollHeight > currentRef.clientHeight);
        } else {
          setIsOverflowing(currentRef.scrollWidth > currentRef.clientWidth);
        }
      }
    };
    // Run checkOverflow after layout using requestAnimationFrame
    const rafId = requestAnimationFrame(() => {
      checkOverflow();
    });

    let resizeObserver: ResizeObserver | undefined;
    if (currentRef && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(currentRef);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [children, isMultiLine]);

  const getTooltipTitle = () => {
    if (tooltipText) {
      return tooltipText;
    }
    if (isOverflowing) {
      return children;
    }
    return undefined;
  };

  /** Tooltip clones the child; a block-level wrapper is required for flex + width:100% ellipsis. */
  const fillFlexRow = !isMultiLine && maxWidth === '100%';

  const typography = (
    <Typography
      noWrap={!isMultiLine}
      textOverflow="ellipsis"
      overflow="hidden"
      maxWidth={maxWidth}
      sx={
        isMultiLine
          ? {
              ...sx,
              display: '-webkit-box',
              WebkitLineClamp: linesNumber,
              WebkitBoxOrient: 'vertical',
            }
          : { ...sx }
      }
      {...props}
      ref={textRef}
    >
      {children}
    </Typography>
  );

  return (
    <Tooltip
      title={getTooltipTitle()}
      enterDelay={delay}
      enterNextDelay={enterNextDelay}
      disableHoverListener={disableHoverListener}
      placement={placement}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        },
      }}
    >
      {fillFlexRow ? (
        <Box component="span" sx={{ display: 'block', width: '100%', minWidth: 0, overflow: 'hidden' }}>
          {typography}
        </Box>
      ) : (
        typography
      )}
    </Tooltip>
  );
};
